import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Loader2, ChevronLeft, ChevronRight, Check, Volume2, Mic, VolumeX, Play, Pause } from 'lucide-react';
import { useAudioTriage } from '@/contexts/AudioTriageContext';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon } from 'lucide-react';
import { filterConditionalQuestions, generateDynamicQuestions } from '@/utils/dynamicQuestions';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { useTriageAssessment } from '@/hooks/useTriageAssessment';
import { TriageData } from '@/types/triage';

// Define screen types for the multi-screen approach
type ScreenType = 'symptom-details' | 'health-info' | 'medical-background' | 'review';

// Microphone Button Component
interface MicrophoneButtonProps {
  onAudioCapture?: (audioData: string) => void;
  fieldName: string;
  size?: 'sm' | 'md';
}

const MicrophoneButton: React.FC<MicrophoneButtonProps> = ({ onAudioCapture, fieldName, size = 'sm' }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleMicClick = async () => {
    if (isRecording) {
      // Stop recording
      setIsRecording(false);
      setIsProcessing(true);
      
      // Simulate audio processing
      setTimeout(() => {
        setIsProcessing(false);
        // For now, just trigger callback with placeholder
        onAudioCapture?.(`Voice input for ${fieldName}`);
      }, 1500);
    } else {
      // Start recording
      setIsRecording(true);
    }
  };

  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';
  const buttonSize = size === 'sm' ? 'h-6 w-6' : 'h-8 w-8';

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleMicClick}
      disabled={isProcessing}
      className={cn(
        buttonSize,
        "p-0 rounded-full transition-colors",
        isRecording 
          ? "bg-red-100 hover:bg-red-200 text-red-600" 
          : "hover:bg-gray-100 text-gray-500"
      )}
      title={isRecording ? "Stop recording" : "Start voice input"}
    >
      {isProcessing ? (
        <Loader2 className={cn(iconSize, "animate-spin")} />
      ) : isRecording ? (
        <div className="relative">
          <Mic className={cn(iconSize, "animate-pulse")} />
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-ping" />
        </div>
      ) : (
        <Mic className={iconSize} />
      )}
    </Button>
  );
};

const FollowUpQuestionScreen: React.FC = () => {
  const navigate = useNavigate();
  const { 
    state,
    setTriageData,
    setTriageResult,
    setCompleted,
    setLoading,
    setError
  } = useAudioTriage();
  const { assessTriage, isAssessing } = useTriageAssessment();

  const [currentScreen, setCurrentScreen] = useState<ScreenType>('symptom-details');
  const [isLoading, setIsLoadingLocal] = useState(false);

  // Get symptoms from state
  const symptoms = state.selectedSymptoms;
  const answers = state.answers;
  const questions = state.questions;

  // Screen configuration
  const screens: { [key in ScreenType]: { title: string; step: number } } = {
    'symptom-details': { title: 'Describe Your Symptoms', step: 1 },
    'health-info': { title: 'Health Information', step: 2 },
    'medical-background': { title: 'Medical Background', step: 3 },
    'review': { title: 'Review & Submit', step: 4 }
  };

  const currentStep = screens[currentScreen].step;
  const totalSteps = 4;
  const progress = (currentStep / totalSteps) * 100;

  // Initialize form data
  const [formData, setFormData] = useState({
    // Symptom details
    severity: '',
    startDate: undefined as Date | undefined,
    onsetType: '',
    
    // Health info
    temperature: '',
    heartRate: '',
    bloodPressure: '',
    oxygenSaturation: '',
    additionalDetails: '',
    
    // Medical background
    age: '',
    gender: '',
    smokingStatus: '',
    medicalHistory: [] as string[],
    medications: '',
    allergies: [] as string[]
  });

  // Navigation functions
  const canGoNext = () => {
    switch (currentScreen) {
      case 'symptom-details':
        return symptoms.length > 0 && formData.severity;
      case 'health-info':
        return true; // All optional
      case 'medical-background':
        return true; // All optional
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    const screenOrder: ScreenType[] = ['symptom-details', 'health-info', 'medical-background', 'review'];
    const currentIndex = screenOrder.indexOf(currentScreen);
    if (currentIndex < screenOrder.length - 1) {
      setCurrentScreen(screenOrder[currentIndex + 1]);
    }
  };

  const handleBack = () => {
    const screenOrder: ScreenType[] = ['symptom-details', 'health-info', 'medical-background', 'review'];
    const currentIndex = screenOrder.indexOf(currentScreen);
    if (currentIndex > 0) {
      setCurrentScreen(screenOrder[currentIndex - 1]);
    }
  };

  const handleSubmit = async () => {
    setIsLoadingLocal(true);
    setLoading(true);
    try {
      // Map form data to TriageData interface structure
      const assessmentData: TriageData = {
        symptoms,
        severity: formData.severity || 'moderate',
        duration: formData.startDate ? `Started ${format(formData.startDate, 'PPP')}` : 'Unknown',
        onset: formData.onsetType || 'gradual',
        additionalInfo: formData.additionalDetails || '',
        vitals: {
          temperature: formData.temperature ? parseFloat(formData.temperature) : undefined,
          heartRate: formData.heartRate ? formData.heartRate.toString() : undefined,
          bloodPressure: formData.bloodPressure || undefined,
          oxygenSaturation: formData.oxygenSaturation ? formData.oxygenSaturation.toString() : undefined,
        },
        age: formData.age ? parseInt(formData.age) : undefined,
        gender: formData.gender || undefined,
        smokingStatus: formData.smokingStatus || undefined,
        medicalHistory: formData.medicalHistory || [],
        medications: formData.medications ? [formData.medications] : [],
        allergies: formData.allergies || [],
      };
      
      // Perform triage assessment with AI processing and database submission
      // This matches the standard triage flow in TriageForm.tsx
      console.log('Starting audio triage assessment...', assessmentData);
      const result = await assessTriage(assessmentData);
      console.log('Audio triage assessment completed:', result);
      
      // Set triage data and result in context
      setTriageData(assessmentData);
      setTriageResult(result);
      setCompleted(true);
      
      // Navigate to results with the real assessment data
      navigate('/patient/audio-triage/results');
    } catch (error) {
      console.error('Error submitting assessment:', error);
      setError('Failed to submit assessment. Please try again.');
    } finally {
      setIsLoadingLocal(false);
      setLoading(false);
    }
  };

  // Render screen content
  const renderScreenContent = () => {
    switch (currentScreen) {
      case 'symptom-details':
        return renderSymptomDetailsScreen();
      case 'health-info':
        return renderHealthInfoScreen();
      case 'medical-background':
        return renderMedicalBackgroundScreen();
      case 'review':
        return renderReviewScreen();
      default:
        return null;
    }
  };

  const renderHealthInfoScreen = () => (
    <div className="space-y-6">
      {/* Health Information */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Health Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Column A: Basic Info */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-base font-medium">
                  Age *
                  <MicrophoneButton 
                    fieldName="age"
                    onAudioCapture={(audio) => {
                      console.log('Audio captured for age:', audio);
                    }}
                  />
                </Label>
                <Input
                  type="number"
                  placeholder="e.g., 34"
                  value={formData.age}
                  onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-base font-medium">
                  Gender
                  <MicrophoneButton 
                    fieldName="gender"
                    onAudioCapture={(audio) => {
                      console.log('Audio captured for gender:', audio);
                    }}
                  />
                </Label>
                <select
                  value={formData.gender}
                  onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-base font-medium">
                  Smoking Status
                  <MicrophoneButton 
                    fieldName="smoking status"
                    onAudioCapture={(audio) => {
                      console.log('Audio captured for smoking status:', audio);
                    }}
                  />
                </Label>
                <select
                  value={formData.smokingStatus}
                  onChange={(e) => setFormData(prev => ({ ...prev, smokingStatus: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select smoking status</option>
                  <option value="never">Never</option>
                  <option value="former">Former</option>
                  <option value="current">Current</option>
                </select>
              </div>
            </div>

            {/* Column B: Vitals (Collapsible) */}
            <div className="space-y-4">
              <div className="border rounded-lg p-4">
                <Label className="text-base font-medium mb-3 block">Vitals (Optional)</Label>
                
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="flex items-center gap-2 text-sm">
                      Temperature (°F)
                      <MicrophoneButton 
                        fieldName="temperature"
                        size="sm"
                        onAudioCapture={(audio) => {
                          console.log('Audio captured for temperature:', audio);
                        }}
                      />
                    </Label>
                    <Input
                      type="number"
                      placeholder="98.6"
                      value={formData.temperature}
                      onChange={(e) => setFormData(prev => ({ ...prev, temperature: e.target.value }))}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500">Normal: 97.7–99.5°F</p>
                  </div>

                  <div className="space-y-1">
                    <Label className="flex items-center gap-2 text-sm">
                      Heart Rate (BPM)
                      <MicrophoneButton 
                        fieldName="heart rate"
                        size="sm"
                        onAudioCapture={(audio) => {
                          console.log('Audio captured for heart rate:', audio);
                        }}
                      />
                    </Label>
                    <Input
                      type="number"
                      placeholder="70"
                      value={formData.heartRate}
                      onChange={(e) => setFormData(prev => ({ ...prev, heartRate: e.target.value }))}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500">Normal: 60–100 BPM</p>
                  </div>

                  <div className="space-y-1">
                    <Label className="flex items-center gap-2 text-sm">
                      Blood Pressure (mmHg)
                      <MicrophoneButton 
                        fieldName="blood pressure"
                        size="sm"
                        onAudioCapture={(audio) => {
                          console.log('Audio captured for blood pressure:', audio);
                        }}
                      />
                    </Label>
                    <Input
                      type="text"
                      placeholder="120/80"
                      value={formData.bloodPressure}
                      onChange={(e) => setFormData(prev => ({ ...prev, bloodPressure: e.target.value }))}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500">Normal: &lt;120/80 mmHg</p>
                  </div>

                  <div className="space-y-1">
                    <Label className="flex items-center gap-2 text-sm">
                      Oxygen Saturation (%)
                      <MicrophoneButton 
                        fieldName="oxygen saturation"
                        size="sm"
                        onAudioCapture={(audio) => {
                          console.log('Audio captured for oxygen saturation:', audio);
                        }}
                      />
                    </Label>
                    <Input
                      type="number"
                      placeholder="98"
                      value={formData.oxygenSaturation}
                      onChange={(e) => setFormData(prev => ({ ...prev, oxygenSaturation: e.target.value }))}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500">Normal: 95–100%</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-base font-medium">
                  Anything else we should know? (Optional)
                  <MicrophoneButton 
                    fieldName="additional details"
                    onAudioCapture={(audio) => {
                      setFormData(prev => ({ ...prev, additionalDetails: audio }));
                    }}
                  />
                </Label>
                <Textarea
                  placeholder="Recent travel, exposures or medical history..."
                  value={formData.additionalDetails}
                  onChange={(e) => setFormData(prev => ({ ...prev, additionalDetails: e.target.value }))}
                  className="w-full min-h-[80px]"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderMedicalBackgroundScreen = () => (
    <div className="space-y-6">
      {/* Medical Background */}
      <Card>
        <CardHeader>
          <CardTitle>Medical Background</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Column A: Medical History */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-base font-medium">
                  Medical History
                  <MicrophoneButton 
                    fieldName="medical history"
                    onAudioCapture={(audio) => {
                      console.log('Audio captured for medical history:', audio);
                    }}
                  />
                </Label>
                <div className="flex flex-wrap gap-2">
                  {['Diabetes', 'Hypertension', 'Heart Disease', 'Asthma', 'COPD', 'Kidney Disease', 'Liver Disease', 'Cancer', 'Depression', 'Anxiety'].map((condition) => (
                    <button
                      key={condition}
                      onClick={() => {
                        const current = formData.medicalHistory;
                        const updated = current.includes(condition)
                          ? current.filter(c => c !== condition)
                          : [...current, condition];
                        setFormData(prev => ({ ...prev, medicalHistory: updated }));
                      }}
                      className={cn(
                        "px-3 py-1 rounded-full text-sm border transition-colors",
                        formData.medicalHistory.includes(condition)
                          ? "bg-indigo-100 border-indigo-300 text-indigo-800"
                          : "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
                      )}
                    >
                      {condition}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-base font-medium">
                  Current Medications
                  <MicrophoneButton 
                    fieldName="medications"
                    onAudioCapture={(audio) => {
                      setFormData(prev => ({ ...prev, medications: audio }));
                    }}
                  />
                </Label>
                <Input
                  placeholder="Search RxNorm or add..."
                  value={formData.medications}
                  onChange={(e) => setFormData(prev => ({ ...prev, medications: e.target.value }))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500">Enter medications separated by commas</p>
              </div>
            </div>

            {/* Column B: Allergies */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-base font-medium">
                  Allergies
                  <MicrophoneButton 
                    fieldName="allergies"
                    onAudioCapture={(audio) => {
                      const allergies = audio.split(',').map(a => a.trim());
                      setFormData(prev => ({ ...prev, allergies }));
                    }}
                  />
                </Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {['Penicillin', 'Nuts', 'Shellfish', 'Latex', 'Pollen', 'Dust'].map((allergy) => (
                    <button
                      key={allergy}
                      onClick={() => {
                        const current = formData.allergies;
                        const updated = current.includes(allergy)
                          ? current.filter(a => a !== allergy)
                          : [...current, allergy];
                        setFormData(prev => ({ ...prev, allergies: updated }));
                      }}
                      className={cn(
                        "px-3 py-1 rounded-full text-sm border transition-colors",
                        formData.allergies.includes(allergy)
                          ? "bg-indigo-100 border-indigo-300 text-indigo-800"
                          : "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
                      )}
                    >
                      {allergy}
                    </button>
                  ))}
                </div>
                <Input
                  placeholder="Add other allergies..."
                  value={formData.allergies.join(', ')}
                  onChange={(e) => setFormData(prev => ({ ...prev, allergies: e.target.value.split(',').map(a => a.trim()) }))}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderReviewScreen = () => (
    <div className="space-y-6">
      {/* Review */}
      <Card>
        <CardHeader>
          <CardTitle>Review Your Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Column A: Symptoms & Details */}
            <div>
              <h4 className="font-medium text-sm text-gray-700 mb-2">Symptoms & Details</h4>
              <div className="space-y-1 text-sm">
                <p><span className="font-medium">Symptoms:</span> {symptoms.join(', ')}</p>
                <p><span className="font-medium">Severity:</span> {formData.severity || 'Not specified'}</p>
                <p><span className="font-medium">Started:</span> {formData.startDate ? format(formData.startDate, 'PPP') : 'Not specified'}</p>
                <p><span className="font-medium">Onset:</span> {formData.onsetType || 'Not specified'}</p>
              </div>
            </div>

            {/* Column B: Health Info */}
            <div>
              <h4 className="font-medium text-sm text-gray-700 mb-2">Health Information</h4>
              <div className="space-y-1 text-sm">
                <p><span className="font-medium">Age:</span> {formData.age || 'Not specified'}</p>
                <p><span className="font-medium">Gender:</span> {formData.gender || 'Not specified'}</p>
                <p><span className="font-medium">Smoking:</span> {formData.smokingStatus || 'Not specified'}</p>
                {formData.temperature && <p><span className="font-medium">Temperature:</span> {parseFloat(formData.temperature).toFixed(1)}°F</p>}
                {formData.heartRate && <p><span className="font-medium">Heart Rate:</span> {formData.heartRate} BPM</p>}
                {formData.bloodPressure && <p><span className="font-medium">Blood Pressure:</span> {formData.bloodPressure}</p>}
                {formData.oxygenSaturation && <p><span className="font-medium">O2 Sat:</span> {formData.oxygenSaturation}%</p>}
              </div>
            </div>

            {/* Medical Background */}
            <div className="md:col-span-2">
              <h4 className="font-medium text-sm text-gray-700 mb-2">Medical Background</h4>
              <div className="space-y-1 text-sm">
                {formData.medicalHistory.length > 0 && (
                  <p><span className="font-medium">Medical History:</span> {formData.medicalHistory.join(', ')}</p>
                )}
                {formData.medications && (
                  <p><span className="font-medium">Medications:</span> {formData.medications}</p>
                )}
                {formData.allergies.length > 0 && (
                  <p><span className="font-medium">Allergies:</span> {formData.allergies.join(', ')}</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>

        <div className="space-y-2">
          <Label className="flex items-center gap-2 text-base font-medium">
            Final Notes (Optional)
            <MicrophoneButton 
              fieldName="final notes"
              onAudioCapture={(audio) => {
                setFormData(prev => ({ ...prev, additionalDetails: audio }));
              }}
            />
          </Label>
          <Textarea
            placeholder="Any additional information you'd like to share..."
            value={formData.additionalDetails}
            onChange={(e) => setFormData(prev => ({ ...prev, additionalDetails: e.target.value }))}
            className="w-full min-h-[80px]"
          />
        </div>
      </Card>
    </div>
  );

  const renderSymptomDetailsScreen = () => (
    <div className="space-y-6">
      {/* Symptom Details - Two Column Layout */}
      <Card>
        <CardHeader>
          <CardTitle>Tell us more about your symptoms</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Column A: Severity */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                How severe are your symptoms?
                <Button variant="ghost" size="sm">
                  <Volume2 className="h-4 w-4" />
                </Button>
                <MicrophoneButton 
                  fieldName="symptom severity"
                  onAudioCapture={(audio) => {
                    // Process audio to determine severity
                    console.log('Audio captured for severity:', audio);
                  }}
                />
              </Label>
              <RadioGroup
                value={formData.severity}
                onValueChange={(value) => setFormData(prev => ({ ...prev, severity: value }))}
                className="space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="mild" id="mild" />
                  <Label htmlFor="mild">Mild</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="moderate" id="moderate" />
                  <Label htmlFor="moderate">Moderate</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="severe" id="severe" />
                  <Label htmlFor="severe">Severe</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="unbearable" id="unbearable" />
                  <Label htmlFor="unbearable">Unbearable</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Column B: Timing */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  When did symptoms start?
                  <Button variant="ghost" size="sm">
                    <Volume2 className="h-4 w-4" />
                  </Button>
                  <MicrophoneButton 
                    fieldName="symptom start date"
                    onAudioCapture={(audio) => {
                      console.log('Audio captured for start date:', audio);
                    }}
                  />
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.startDate ? format(formData.startDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.startDate}
                      onSelect={(date) => setFormData(prev => ({ ...prev, startDate: date }))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  How did symptoms begin?
                  <Button variant="ghost" size="sm">
                    <Volume2 className="h-4 w-4" />
                  </Button>
                  <MicrophoneButton 
                    fieldName="symptom onset"
                    onAudioCapture={(audio) => {
                      console.log('Audio captured for onset:', audio);
                    }}
                  />
                </Label>
                <select
                  value={formData.onsetType}
                  onChange={(e) => setFormData(prev => ({ ...prev, onsetType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select onset type</option>
                  <option value="sudden">Sudden onset</option>
                  <option value="gradual">Gradual onset</option>
                  <option value="intermittent">Comes and goes</option>
                  <option value="constant">Constant</option>
                </select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-100 p-4 sm:p-6">
      <div className="w-full max-w-5xl bg-white p-6 rounded-xl shadow-2xl">
        {/* Header with Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900">{screens[currentScreen].title}</h1>
            <div className="text-sm text-gray-500">Step {currentStep} of {totalSteps}</div>
          </div>
          
          {/* Horizontal Stepper */}
          <div className="flex items-center justify-between mb-4">
            {Object.entries(screens).map(([screenKey, screen], index) => (
              <div key={screenKey} className="flex items-center">
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                  screen.step <= currentStep 
                    ? "bg-indigo-600 text-white" 
                    : "bg-gray-200 text-gray-600"
                )}>
                  {screen.step < currentStep ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    screen.step
                  )}
                </div>
                {index < Object.keys(screens).length - 1 && (
                  <div className={cn(
                    "w-16 h-0.5 mx-2",
                    screen.step < currentStep ? "bg-indigo-600" : "bg-gray-200"
                  )} />
                )}
              </div>
            ))}
          </div>
          
          {/* Progress Bar */}
          <Progress value={progress} className="w-full" />
        </div>

        {/* Screen Content */}
        <div className="mb-8">
          {renderScreenContent()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center pt-6 border-t">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentScreen === 'symptom-details'}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>

          <div className="flex gap-2">
            {currentScreen === 'review' ? (
              <Button
                onClick={handleSubmit}
                disabled={isLoading || isAssessing}
                className="flex items-center gap-2"
              >
                {(isLoading || isAssessing) && <Loader2 className="h-4 w-4 animate-spin" />}
                {(isLoading || isAssessing) ? 'Submitting Assessment...' : 'Submit Assessment'}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={!canGoNext()}
                className="flex items-center gap-2"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FollowUpQuestionScreen;
